import React, { useEffect, useState } from "react";
import { fetchFeedXml, FeedUnavailableError } from "../utils/feedProxy";

// Inline rather than pulling in the FontAwesome runtime (~60 kB minified) for a
// single glyph. Matches the inline-SVG convention used by the other widgets.
const InfoCircleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 512 512" fill="currentColor" aria-hidden="true" className={className}>
    <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336h24V272H216c-13.3 0-24-10.7-24-24s10.7-24 24-24h48c13.3 0 24 10.7 24 24v88h8c13.3 0 24 10.7 24 24s-10.7 24-24 24H216c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-208a32 32 0 1 1 0 64 32 32 0 1 1 0-64z" />
  </svg>
);

interface FeedItem {
  title: string;
  pubDate: string;
  imageUrl: string;
  description: string;
  link: string;
}

const NASAPicOfDayWidget: React.FC = () => {
  const [item, setItem] = useState<FeedItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function fetchFeed() {
      setLoading(true);
      try {
        const xml = await fetchFeedXml("nasa-feed");
        const firstItem = xml.querySelector("item");
        if (!firstItem) throw new Error("Feed contained no images");
        // Prefer enclosure image if present
        let imageUrl = "";
        const enclosure = firstItem.querySelector("enclosure");
        if (enclosure && enclosure.getAttribute("type")?.startsWith("image/")) {
          imageUrl = enclosure.getAttribute("url") || "";
        } else {
          const description = firstItem.querySelector("description")?.textContent || "";
          const imgMatch = description.match(/<img[^>]+src=["']([^"']+)["']/i);
          if (imgMatch) imageUrl = imgMatch[1];
        }
        const title = firstItem.querySelector("title")?.textContent || "";
        const pubDate = firstItem.querySelector("pubDate")?.textContent || "";
        const link = firstItem.querySelector("link")?.textContent || "";
        let description = firstItem.querySelector("description")?.textContent || "";
        description = description.replace(/<[^>]+>/g, "").trim();
        setItem({ title, pubDate, imageUrl, description, link });
      } catch (err) {
        if (err instanceof FeedUnavailableError) {
          console.error(`NASA widget: ${err.message}`, err.detail ?? "");
          setError(err.message);
        } else {
          console.error("NASA widget: unexpected failure", err);
          setError("Failed to load NASA Pic of the Day");
        }
      } finally {
        setLoading(false);
      }
    }
    fetchFeed();
  }, []);

  return (
    <div className="rounded-[var(--radius-md)] bg-[var(--color-bg-secondary)] p-0 w-full h-full flex flex-col overflow-hidden">
      {error && (
        <div className="flex items-center gap-2 text-xs text-[var(--color-warning)] px-4 pt-4">
          <InfoCircleIcon className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {item && !loading && item.imageUrl && (
        <a
          href={item.link}
          target="_blank"
          rel="noopener noreferrer"
          className="block flex-1 min-h-0"
        >
          <img
            src={item.imageUrl}
            alt={item.title}
            className="w-full h-64 object-cover bg-black"
            style={{ maxHeight: "60vh" }}
          />
        </a>
      )}
      {item && !loading && (
        <div className="p-4 flex flex-col gap-1">
          <a
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            className="font-heading font-semibold text-base text-[var(--color-text-primary)] hover:underline truncate"
            title={item.title}
          >
            {item.title}
          </a>
          {item.description && (
            <div
              className="text-xs text-[var(--color-text-tertiary)] line-clamp-2"
              title={item.description}
            >
              {item.description}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NASAPicOfDayWidget;
